import { useFeatureFlagContext } from '@/context/featureFlagContext';
import { useListContactsQuery, useUpdateContactMutation } from '@/features/contacts/contactsApi';
import { useGetUserDetailsQuery } from '@/features/user/userApi';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';
import { createValidationSchema } from './clientDetailData';
import { NumericFormat } from 'react-number-format';
import {
	Button,
	Checkbox,
	Chip,
	CircularProgress,
	FormControlLabel,
	FormHelperText,
	FormLabel,
	Stack,
	TextField,
} from '@mui/material';
import { ResetButton } from './resetButton';
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFnsV3';
import { showToast } from '@/features/toast/toastSlice';
import { safelyParseDate } from '@/utils/date';
import { format } from 'date-fns';

const today = new Date();

const UpdateContactField = ({
	header,
	title = '',
	readOnly,
	contact,
	disabled = false,
	handleOpenDialog = null,
}) => {
	const ref = useRef();
	const dispatch = useDispatch();
	const params = useParams();

	const [value, setValue] = useState(null);
	const [error, setError] = useState(false);

	const clientId = contact?.clientId ?? params?.clientId;
	const { data: contacts, ...contactQuery } = useListContactsQuery(clientId, {
		skip: !clientId,
		selectFromResult: ({ data, ...rest }) => {
			const contacts = data ?? [];
			return {
				...rest,
				data: contacts,
			};
		},
	});

	const initialValue = useMemo(() => contact?.[header], [header, contact?.[header]]);

	useEffect(() => {
		setValue(initialValue);
	}, [initialValue]);

	const { getFeatureFlags, isSuccess } = useFeatureFlagContext();
	const contactsEmailMandatory = Boolean(getFeatureFlags('contactNonRequired')?.active);

	const isLoading = contactQuery.isLoading || contactQuery.isUninitialized || !isSuccess;

	const [updateContact, contactState] = useUpdateContactMutation();

	const isUpdating = contactState.isLoading;

	const loading = isLoading || isUpdating;

	const handleReset = (event) => {
		event.preventDefault();
		event.stopPropagation();
		setValue(initialValue);
	};

	const handleBlur = async (event) => {
		setError('');
		if (!readOnly && event?.target?.value !== initialValue) {
			await handleSubmit();
		}
	};

	const handleRemoveContactAssociation = () => {
		if (handleOpenDialog && contact?.Id) {
			handleOpenDialog(contact.Id, contact.isMainContact);
		}
	};

	const validationSchema = useMemo(
		() =>
			createValidationSchema([header], {
				skipEmail: ['email', 'Email'].includes(header) && contactsEmailMandatory,
			}),
		[header, contactsEmailMandatory],
	);

	const handleSubmit = async (newValue) => {
		console.log('Handle submit', { header, newValue, value });
		setError('');

		let v = newValue != null ? newValue : value;
		//Check if value is string before splitting...
		if (typeof v === 'string') {
			v = v.trim();
		}

		if (!contact) return;
		await validationSchema
			.validate({ [header]: v })
			.then(async (properties) => {
				console.log('Updating contact:', properties);

				if (['Name', 'Addressee'].includes(header)) {
					setTimeout(() => {
						ref.current?.scrollIntoView({
							behavior: 'smooth',
							block: 'center',
						});
					}, 10);
				}
				const contactRes = await updateContact({
					insightId: contact.Id,
					clientId,
					[header === 'IsPrimary' ? 'isMainContact' : header]:
						header === 'BirthDate' && v == null ? '' : v,
				})
					.unwrap()
					.catch((e) => {
						console.error(`Error updating contact ${header}:`, e);
						dispatch(
							showToast({
								message: `Error updating contact: ${e.data?.message ?? e.message ?? 'Unknown error'}`,
								error: true,
							}),
						);
						setError(e.data?.message ?? e.message ?? 'Unknown error');
						if (['Name', 'Addressee'].includes(header)) {
							setTimeout(() => {
								ref.current?.scrollIntoView({
									behavior: 'smooth',
									block: 'center',
								});
							}, 0);
						}
					});
				console.log('Contact update response', contactRes);
			})
			.catch((e) => {
				console.error(`Error with validation ${header}:`, e);
				setError(e.message);
			});
	};

	const changeValue = (value) => {
		setValue(value);
		console.log('Change value', header, value);
		setTimeout(() => {
			handleSubmit(value);
		}, 100);
	};

	const drawComponent = () => {
		switch (header) {
			case 'Telephone':
			case 'Telephone2':
				return (
					<NumericFormat
						customInput={TextField}
						fullWidth
						decimalScale={0}
						allowNegative={false}
						allowLeadingZeros
						value={value}
						onValueChange={({ value }) => setValue(value)}
						disabled={loading}
						name={header}
						error={Boolean(error) && !contactsEmailMandatory}
						onBlur={handleBlur}
						InputProps={{
							endAdornment: (
								<ResetButton isLoading={loading} onMouseDown={handleReset} />
							),
						}}
					/>
				);

			case 'BirthDate':
				return (
					<LocalizationProvider dateAdapter={AdapterDateFns}>
						<DatePicker
							value={safelyParseDate(value)}
							onChange={(newValue) => {
								if (newValue) {
									const formattedDate = format(newValue, 'yyyy-MM-dd');
									setValue(formattedDate);
									handleSubmit(formattedDate);
								} else {
									setValue(null);
									handleSubmit('');
								}
							}}
							slotProps={{
								field: { clearable: true },
								textField: {
									error: Boolean(error),
									helperText: error,
								},
							}}
							format='dd/MM/yyyy'
							sx={{ width: '100%' }}
							maxDate={today}
							disabled={loading}
						/>
					</LocalizationProvider>
				);
			case 'isMainContact':
			case 'IsPrimary':
				return contacts?.length > 0 ? (
					<Stack>
						<FormControlLabel
							control={
								<Checkbox
									checked={value}
									onChange={async (event) => {
										const newValue = event.target.checked;

										if (contacts) {
											const currentlySelected = contacts.filter(
												(c) => c.isMainContact,
											);
											if (
												!newValue &&
												currentlySelected.length === 1 &&
												currentlySelected[0].Id === contact?.Id
											) {
												return;
											}
											changeValue(newValue);
										}
									}}
									disabled={loading}
								/>
							}
							label='Primary contact'
						/>
						{contacts.length > 1 && (
							<Button
								variant='outlined'
								color='error'
								onClick={handleRemoveContactAssociation}
								sx={{
									// 	backgroundColor: 'white',
									// 	color: 'red',
									// 	border: '1px solid red',
									borderRadius: '12px',
									// 	textTransform: 'uppercase',
									// 	fontWeight: 'bold',
									// 	fontSize: '0.75rem',
									// 	px: 2,
									// 	py: 1,
									'&:hover': {
										backgroundColor: 'red',
										color: 'white',
										borderColor: 'red',
										// border: 'none',
									},
								}}
							>
								{'Remove'}
							</Button>
						)}
					</Stack>
				) : null;

			default:
				return (
					<TextField
						fullWidth
						InputProps={{
							readOnly,
							endAdornment: (
								<>
									{isUpdating ? (
										<CircularProgress size={20} />
									) : (
										!readOnly && (
											<ResetButton
												isLoading={loading}
												onMouseDown={handleReset}
											/>
										)
									)}
								</>
							),
						}}
						value={value ?? ''}
						onChange={(e) => setValue(e.target.value)}
						error={Boolean(error)}
						onBlur={handleBlur}
						disabled={
							header === 'name'
								? true
								: disabled && !['email', 'firstName', 'lastName'].includes(header)
						}
					/>
				);
		}
	};

	return (
		<div ref={ref}>
			<FormLabel>{title}</FormLabel>
			{drawComponent()}
			{Boolean(error) && <FormHelperText error>{error}</FormHelperText>}
		</div>
	);
};
export default UpdateContactField;
